import React, { useState, useCallback, useMemo } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import './SalesReport.css';
import Spinner from './Spinner';
import Notification from './Notification';
import useGlobalTheme from '../hooks/useGlobalTheme';
import { formatDateForLabel } from './salesReportUtils';
import { isNetInvoiced } from '../utils/invoiceStatus';
import {
  SalesReportDashboardView,
  SalesReportDemographicView,
  SalesReportCustomerView,
  SalesReportProductView,
  SalesReportTemporalView,
} from './SalesReportSubviews';

const getDefaultStartDate = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}-01`;
};
const getDefaultEndDate = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const lastDay = new Date(y, m + 1, 0).getDate();
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
};

const SalesReport = () => {
  const { isDarkMode } = useGlobalTheme();
  const chartTextColor = isDarkMode ? '#e0e0e0' : '#0f172a';
  const uiTextColor = isDarkMode ? '#e0e0e0' : '#334155';
  const [orderSales, setOrderSales] = useState([]);
  const [mercadoPagoSales, setMercadoPagoSales] = useState([]);
  const [physicalSales, setPhysicalSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ message: '', show: false });
  const [activeTab, setActiveTab] = useState('dashboard');
  const [startDate, setStartDate] = useState(getDefaultStartDate);
  const [endDate, setEndDate] = useState(getDefaultEndDate);
  const [customerChannelTab, setCustomerChannelTab] = useState('pedidos');
  const [categoryHighlightMetric, setCategoryHighlightMetric] = useState('quantity'); // 'quantity' | 'revenue' | 'profit'
  const [categoryHighlightCount, setCategoryHighlightCount] = useState(3);
  const [productHighlightMetric, setProductHighlightMetric] = useState('quantity'); // 'quantity' | 'revenue' | 'profit'
  const [productHighlightCount, setProductHighlightCount] = useState(5);
  const [productTableSort, setProductTableSort] = useState('revenue');
  const [categoryTableSort, setCategoryTableSort] = useState('revenue');
  const [detailedAnalysisTab, setDetailedAnalysisTab] = useState('product');
  const [detailedItemsPerPage, setDetailedItemsPerPage] = useState(10);
  const [productTablePage, setProductTablePage] = useState(1);
  const [categoryTablePage, setCategoryTablePage] = useState(1);

  const fetchSalesData = useCallback(async (overrideStart, overrideEnd) => {
    const fromDate = overrideStart != null ? overrideStart : startDate;
    const toDate = overrideEnd != null ? overrideEnd : endDate;
    setLoading(true);
    try {
      const start = fromDate ? new Date(fromDate) : null;
      const end = toDate ? new Date(toDate) : null;
      const hasValidRange = start && end && !isNaN(start.getTime()) && !isNaN(end.getTime());

      const fetchOrders = async () => {
        const ordersCollection = collection(db, 'pedidos');
        let ordersQuery = query(ordersCollection, orderBy('date', 'desc'));

        if (hasValidRange) {
          start.setUTCHours(0, 0, 0, 0);
          end.setUTCHours(23, 59, 59, 999);
          ordersQuery = query(ordersCollection, orderBy('date', 'desc'), where('date', '>=', start), where('date', '<=', end));
        }

        const ordersSnapshot = await getDocs(ordersQuery);
        const safeDate = (d) => {
          const parsed = d?.toDate?.();
          return parsed instanceof Date && !isNaN(parsed.getTime()) ? parsed : null;
        };
        return ordersSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            orderNumber: data.orderNumber,
            customerName: data.nombre,
            address: `${data.domicilio}, ${data.localidad}, ${data.codigoPostal}, ${data.provincia}, ${data.pais}`,
            email: data.email,
            phone: data.telefono,
            localidad: data.localidad,
            provincia: data.provincia,
            pais: data.pais,
            codigoPostal: data.codigoPostal,
            ...data,
            date: safeDate(data.date),
            cartItems: data.cartItems?.map(item => ({
              ...item,
              image: item.images && item.images[0] ? item.images[0] : '',
              costPrice: item.costPrice,
              price: item.price
            })),
            isConfirmed: data.isConfirmed || false,
            channel: 'pedidos',
            isInvoiced: isNetInvoiced(data),
            invoicedAmount: isNetInvoiced(data) ? (parseFloat(data.totalPrice) || 0) : 0,
            productsCount: (data.cartItems || []).reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0)
          };
        }).filter(order => order.isConfirmed);
      };

      const fetchMercadoPago = async () => {
        const mercadoPagoCollection = collection(db, 'compras_mercadopago');
        let mercadoPagoQuery = query(mercadoPagoCollection, orderBy('date', 'desc'));

        if (hasValidRange) {
          const startMp = new Date(fromDate);
          startMp.setUTCHours(0, 0, 0, 0);
          const endMp = new Date(toDate);
          endMp.setUTCHours(23, 59, 59, 999);
          mercadoPagoQuery = query(mercadoPagoCollection, orderBy('date', 'desc'), where('date', '>=', startMp), where('date', '<=', endMp));
        }

        const mercadoPagoSnapshot = await getDocs(mercadoPagoQuery);
        const safeDateMp = (d) => {
          const parsed = d?.toDate?.();
          return parsed instanceof Date && !isNaN(parsed.getTime()) ? parsed : null;
        };
        return mercadoPagoSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            orderNumber: data.orderNumber,
            customerName: data.nombre,
            address: `${data.domicilio}, ${data.localidad}, ${data.codigoPostal}, ${data.provincia}, ${data.pais}`,
            email: data.email,
            phone: data.telefono,
            localidad: data.localidad,
            provincia: data.provincia,
            pais: data.pais,
            codigoPostal: data.codigoPostal,
            ...data,
            date: safeDateMp(data.date),
            cartItems: data.cartItems?.map(item => ({
              ...item,
              image: item.images && item.images[0] ? item.images[0] : '',
              costPrice: item.costPrice || 0,
              price: item.price || 0,
              quantity: item.quantity || 0
            })),
            isConfirmed: data.isConfirmed || false,
            channel: 'mercadopago',
            isInvoiced: isNetInvoiced(data),
            invoicedAmount: isNetInvoiced(data) ? (parseFloat(data.totalPrice) || 0) : 0,
            productsCount: (data.cartItems || []).reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0)
          };
        }).filter(sale => sale.isConfirmed);
      };

      const fetchPhysicalSales = async () => {
        const physicalSalesCollection = collection(db, 'ventas_fisicas');
        let physicalSalesQuery = query(physicalSalesCollection, orderBy('date', 'desc'), where('isConfirmed', '==', true));

        if (hasValidRange) {
          const startPhys = new Date(fromDate);
          startPhys.setUTCHours(0, 0, 0, 0);
          const endPhys = new Date(toDate);
          endPhys.setUTCHours(23, 59, 59, 999);
          physicalSalesQuery = query(physicalSalesCollection, orderBy('date', 'desc'), where('date', '>=', startPhys), where('date', '<=', endPhys), where('isConfirmed', '==', true));
        }

        const physicalSalesSnapshot = await getDocs(physicalSalesQuery);
        const safeDatePhys = (d) => {
          const parsed = d?.toDate?.();
          return parsed instanceof Date && !isNaN(parsed.getTime()) ? parsed : null;
        };
        return physicalSalesSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            orderNumber: data.orderNumber,
            customerName: data.customerData?.name || 'Cliente Físico',
            address: `${data.customerData?.cuit || ''}, ${data.customerData?.invoiceType || ''}`,
            email: data.customerData?.email || '', // Ahora capturamos el email correctamente
            phone: data.customerData?.phone || '',
            localidad: data.customerData?.city || 'Tienda Física',
            provincia: data.customerData?.province || 'Local',
            pais: data.customerData?.country || 'Argentina',
            ...data,
            date: safeDatePhys(data.date),
            cartItems: data.products?.map(item => ({
              ...item,
              image: item.images && item.images[0] ? item.images[0] : '',
              costPrice: item.costPrice || 0,
              price: item.price || 0,
              quantity: item.quantity || 0
            })),
            isConfirmed: true,
            channel: 'fisicas',
            isInvoiced: isNetInvoiced(data),
            invoicedAmount: isNetInvoiced(data) ? (parseFloat(data.totalPrice) || 0) : 0,
            productsCount: (data.products || []).reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0)
          };
        });
      };

      const [ordersList, mercadoPagoList, physicalSalesList] = await Promise.all([fetchOrders(), fetchMercadoPago(), fetchPhysicalSales()]);

      setOrderSales(ordersList);
      setMercadoPagoSales(mercadoPagoList);
      setPhysicalSales(physicalSalesList);
    } catch (error) {
      console.error("Error fetching sales data:", error);
      setNotification({ message: 'Error al cargar datos de ventas.', show: true });
    }
    setLoading(false);
  }, [startDate, endDate]);

  // Análisis avanzado y optimizado de datos
  const analyticsData = useMemo(() => {
    const allSales = [...orderSales, ...mercadoPagoSales, ...physicalSales];
    
    // Función optimizada para calcular métricas (incl. productos, AFIP)
    const calculateMetrics = (sales) => {
      let revenue = 0;
      let cost = 0;
      let orderCount = sales.length;
      let productsCount = 0;
      let invoicedAmount = 0;
      let invoicedCount = 0;

      for (const sale of sales) {
        revenue += parseFloat(sale.totalPrice) || 0;
        productsCount += sale.productsCount || 0;
        if (sale.isInvoiced) {
          invoicedAmount += sale.invoicedAmount || parseFloat(sale.totalPrice) || 0;
          invoicedCount += 1;
        }
        if (sale.cartItems) {
          for (const item of sale.cartItems) {
            cost += (parseFloat(item.costPrice) || 0) * (parseInt(item.quantity) || 0);
          }
        }
        // El costo de envío se imputa al costo (lo pagamos al transporte), no es ganancia.
        cost += parseFloat(sale.shippingCost) || 0;
      }

      const profit = revenue - cost;
      const margin = revenue > 0 ? (profit / revenue) * 100 : 0;
      const avgOrderValue = orderCount > 0 ? revenue / orderCount : 0;

      return { revenue, cost, profit, margin, avgOrderValue, orderCount, productsCount, invoicedAmount, invoicedCount };
    };

    // Métricas por canal
    const channelMetrics = {
      orders: calculateMetrics(orderSales),
      mercadoPago: calculateMetrics(mercadoPagoSales),
      physical: calculateMetrics(physicalSales),
      total: calculateMetrics(allSales)
    };

    // Análisis optimizado con un solo recorrido
    const demographicAnalysis = {
      byProvince: {},
      byCity: {},
      productsByLocation: {}
    };

    const customersByChannel = {
      pedidos: {},
      mercadopago: {},
      fisicas: {}
    };

    const productAnalysis = {};
    const categoryAnalysis = {};
    const categoryMonthlyAnalysis = {};
    const productMonthlyAnalysis = {};
    const temporalAnalysis = {
      daily: {},
      monthly: {},
      hourly: {},
      dayOfWeek: { 0: { revenue: 0, orders: 0, products: 0 }, 1: { revenue: 0, orders: 0, products: 0 }, 2: { revenue: 0, orders: 0, products: 0 }, 3: { revenue: 0, orders: 0, products: 0 }, 4: { revenue: 0, orders: 0, products: 0 }, 5: { revenue: 0, orders: 0, products: 0 }, 6: { revenue: 0, orders: 0, products: 0 } }
    };

    // Recorrido único optimizado
    for (const sale of allSales) {
      const province = sale.provincia || 'No especificado';
      const city = sale.localidad || 'No especificado';
      const customerKey = sale.email || sale.customerName || 'Anónimo';
      const saleValue = parseFloat(sale.totalPrice) || 0;
      const channelKey = sale.channel === 'pedidos' ? 'pedidos' : 
                        sale.channel === 'mercadopago' ? 'mercadopago' : 'fisicas';

      // Demografía por provincia
      if (!demographicAnalysis.byProvince[province]) {
        demographicAnalysis.byProvince[province] = { 
          sales: 0, revenue: 0, customers: new Set(), orders: 0, avgOrderValue: 0,
          products: {}
        };
      }
      demographicAnalysis.byProvince[province].sales += saleValue;
      demographicAnalysis.byProvince[province].revenue += saleValue;
      demographicAnalysis.byProvince[province].customers.add(customerKey);
      demographicAnalysis.byProvince[province].orders += 1;

      // Demografía por ciudad
      if (!demographicAnalysis.byCity[city]) {
        demographicAnalysis.byCity[city] = { 
          sales: 0, revenue: 0, customers: new Set(), orders: 0, province: province,
          products: {}
        };
      }
      demographicAnalysis.byCity[city].sales += saleValue;
      demographicAnalysis.byCity[city].revenue += saleValue;
      demographicAnalysis.byCity[city].customers.add(customerKey);
      demographicAnalysis.byCity[city].orders += 1;

      // Análisis de clientes por canal
      const validDate = sale.date && sale.date instanceof Date && !isNaN(sale.date.getTime());

      if (!customersByChannel[channelKey][customerKey]) {
        customersByChannel[channelKey][customerKey] = {
          name: sale.customerName,
          email: sale.email,
          phone: sale.phone,
          location: `${city}, ${province}`,
          totalSpent: 0,
          orderCount: 0,
          avgOrderValue: 0,
          firstPurchase: validDate ? sale.date : null,
          lastPurchase: validDate ? sale.date : null,
          productsBought: new Set()
        };
      }
      
      customersByChannel[channelKey][customerKey].totalSpent += saleValue;
      customersByChannel[channelKey][customerKey].orderCount += 1;
      customersByChannel[channelKey][customerKey].avgOrderValue = 
        customersByChannel[channelKey][customerKey].totalSpent / 
        customersByChannel[channelKey][customerKey].orderCount;
      
      if (validDate) {
        const cust = customersByChannel[channelKey][customerKey];
        if (cust.lastPurchase == null || sale.date > cust.lastPurchase) cust.lastPurchase = sale.date;
        if (cust.firstPurchase == null || sale.date < cust.firstPurchase) cust.firstPurchase = sale.date;
      }

      // Análisis temporal
      if (validDate) {
        const day = sale.date.toISOString().split('T')[0];
        const month = `${sale.date.getFullYear()}-${String(sale.date.getMonth() + 1).padStart(2, '0')}`;
        const hour = sale.date.getHours();
        const dayOfWeek = sale.date.getDay();

        if (!temporalAnalysis.daily[day]) {
          temporalAnalysis.daily[day] = { revenue: 0, orders: 0, products: 0 };
        }
        temporalAnalysis.daily[day].revenue += saleValue;
        temporalAnalysis.daily[day].orders += 1;
        temporalAnalysis.daily[day].products += sale.productsCount || 0;

        if (!temporalAnalysis.monthly[month]) {
          temporalAnalysis.monthly[month] = { revenue: 0, orders: 0, products: 0 };
        }
        temporalAnalysis.monthly[month].revenue += saleValue;
        temporalAnalysis.monthly[month].orders += 1;
        temporalAnalysis.monthly[month].products += sale.productsCount || 0;

        if (!temporalAnalysis.hourly[hour]) {
          temporalAnalysis.hourly[hour] = { revenue: 0, orders: 0, products: 0 };
        }
        temporalAnalysis.hourly[hour].revenue += saleValue;
        temporalAnalysis.hourly[hour].orders += 1;
        temporalAnalysis.hourly[hour].products += sale.productsCount || 0;

        temporalAnalysis.dayOfWeek[dayOfWeek].revenue += saleValue;
        temporalAnalysis.dayOfWeek[dayOfWeek].orders += 1;
        temporalAnalysis.dayOfWeek[dayOfWeek].products += sale.productsCount || 0;
      }

      // Análisis de productos
      if (sale.cartItems) {
        for (const item of sale.cartItems) {
          const productKey = item.model || 'Producto sin nombre';
          const category = item.category || 'Sin categoría';
          const quantity = parseInt(item.quantity) || 0;
          const revenue = (parseFloat(item.price) || 0) * quantity;
          const cost = (parseFloat(item.costPrice) || 0) * quantity;

          const monthKey = validDate
            ? `${sale.date.getFullYear()}-${String(sale.date.getMonth() + 1).padStart(2, '0')}`
            : null;

          // Productos por ubicación
          if (!demographicAnalysis.byProvince[province].products[productKey]) {
            demographicAnalysis.byProvince[province].products[productKey] = {
              quantity: 0, revenue: 0, orders: 0
            };
          }
          demographicAnalysis.byProvince[province].products[productKey].quantity += quantity;
          demographicAnalysis.byProvince[province].products[productKey].revenue += revenue;
          demographicAnalysis.byProvince[province].products[productKey].orders += 1;

          if (!demographicAnalysis.byCity[city].products[productKey]) {
            demographicAnalysis.byCity[city].products[productKey] = {
              quantity: 0, revenue: 0, orders: 0
            };
          }
          demographicAnalysis.byCity[city].products[productKey].quantity += quantity;
          demographicAnalysis.byCity[city].products[productKey].revenue += revenue;
          demographicAnalysis.byCity[city].products[productKey].orders += 1;

          // Análisis de productos
          if (!productAnalysis[productKey]) {
            productAnalysis[productKey] = {
              totalQuantity: 0,
              totalRevenue: 0,
              totalCost: 0,
              orderCount: 0,
              avgPrice: 0,
              customers: new Set(),
              channels: { pedidos: 0, mercadopago: 0, fisicas: 0 }
            };
          }
          
          productAnalysis[productKey].totalQuantity += quantity;
          productAnalysis[productKey].totalRevenue += revenue;
          productAnalysis[productKey].totalCost += cost;
          productAnalysis[productKey].orderCount += 1;
          productAnalysis[productKey].avgPrice = productAnalysis[productKey].totalRevenue / productAnalysis[productKey].totalQuantity;
          productAnalysis[productKey].customers.add(sale.email || sale.customerName);
          productAnalysis[productKey].channels[sale.channel] += quantity;
          
          // Análisis por categoría
          if (!categoryAnalysis[category]) {
            categoryAnalysis[category] = { 
              revenue: 0, 
              quantity: 0, 
              cost: 0,
              products: new Set(),
              customers: new Set()
            };
          }
          categoryAnalysis[category].revenue += revenue;
          categoryAnalysis[category].quantity += quantity;
          categoryAnalysis[category].cost += cost;
          categoryAnalysis[category].products.add(productKey);
          categoryAnalysis[category].customers.add(sale.email || sale.customerName);

          if (monthKey) {
            if (!categoryMonthlyAnalysis[category]) {
              categoryMonthlyAnalysis[category] = {};
            }
            if (!categoryMonthlyAnalysis[category][monthKey]) {
              categoryMonthlyAnalysis[category][monthKey] = {
                quantity: 0,
                revenue: 0,
                cost: 0
              };
            }
            categoryMonthlyAnalysis[category][monthKey].quantity += quantity;
            categoryMonthlyAnalysis[category][monthKey].revenue += revenue;
            categoryMonthlyAnalysis[category][monthKey].cost += cost;

            if (!productMonthlyAnalysis[productKey]) {
              productMonthlyAnalysis[productKey] = {};
            }
            if (!productMonthlyAnalysis[productKey][monthKey]) {
              productMonthlyAnalysis[productKey][monthKey] = {
                quantity: 0,
                revenue: 0,
                cost: 0
              };
            }
            productMonthlyAnalysis[productKey][monthKey].quantity += quantity;
            productMonthlyAnalysis[productKey][monthKey].revenue += revenue;
            productMonthlyAnalysis[productKey][monthKey].cost += cost;
          }

          customersByChannel[channelKey][customerKey].productsBought.add(productKey);
        }
      }
    }

    // Calcular valores promedio para demografía
    for (const province in demographicAnalysis.byProvince) {
      const data = demographicAnalysis.byProvince[province];
      data.avgOrderValue = data.revenue / data.orders;
      data.customerCount = data.customers.size;
    }

    for (const city in demographicAnalysis.byCity) {
      const data = demographicAnalysis.byCity[city];
      data.avgOrderValue = data.revenue / data.orders;
      data.customerCount = data.customers.size;
    }

    // Análisis de clientes general
    const customerAnalysis = {};
    for (const channel of Object.values(customersByChannel)) {
      for (const [key, customer] of Object.entries(channel)) {
        if (!customerAnalysis[key] || customer.totalSpent > customerAnalysis[key].totalSpent) {
          customerAnalysis[key] = customer;
        }
      }
    }

    // Segmentación de clientes
    const customerSegments = {
      vip: [],
      regular: [],
      occasional: [],
      single: []
    };

    for (const customer of Object.values(customerAnalysis)) {
      if (customer.totalSpent > 50000) {
        customerSegments.vip.push(customer);
      } else if (customer.totalSpent >= 10000 && customer.totalSpent <= 50000) {
        customerSegments.regular.push(customer);
      } else if (customer.totalSpent < 10000 && customer.orderCount > 1) {
        customerSegments.occasional.push(customer);
      } else if (customer.orderCount === 1) {
        customerSegments.single.push(customer);
      }
    }

    // Top productos optimizado
    const productsArray = Object.entries(productAnalysis).map(([name, data]) => ({
      name,
      ...data,
      profit: data.totalRevenue - data.totalCost,
      profitMargin: data.totalRevenue > 0 ? ((data.totalRevenue - data.totalCost) / data.totalRevenue) * 100 : 0,
      customerCount: data.customers.size
    }));

    const topProducts = {
      byRevenue: [...productsArray].sort((a, b) => b.totalRevenue - a.totalRevenue).slice(0, 10),
      byProfit: [...productsArray].sort((a, b) => b.profit - a.profit).slice(0, 10),
      byQuantity: [...productsArray].sort((a, b) => b.totalQuantity - a.totalQuantity).slice(0, 10)
    };

    // Top categorías con métricas completas
    const categoriesArray = Object.entries(categoryAnalysis).map(([name, data]) => ({
      name,
      totalQuantity: data.quantity,
      totalRevenue: data.revenue,
      totalCost: data.cost,
      profit: data.revenue - data.cost,
      profitMargin: data.revenue > 0 ? ((data.revenue - data.cost) / data.revenue) * 100 : 0,
      avgPrice: data.quantity > 0 ? data.revenue / data.quantity : 0,
      customerCount: data.customers.size,
      productCount: data.products.size
    }));

    const topCategories = {
      byRevenue: [...categoriesArray].sort((a, b) => b.totalRevenue - a.totalRevenue),
      byProfit: [...categoriesArray].sort((a, b) => b.profit - a.profit),
      byQuantity: [...categoriesArray].sort((a, b) => b.totalQuantity - a.totalQuantity)
    };

    return {
      channelMetrics,
      demographicAnalysis,
      customerAnalysis,
      customerSegments,
      customersByChannel,
      productAnalysis,
      categoryAnalysis,
      topProducts,
      topCategories,
      temporalAnalysis,
      categoryMonthlyAnalysis,
      productMonthlyAnalysis
    };
  }, [orderSales, mercadoPagoSales, physicalSales]);

  const actualDateRange = useMemo(() => {
    const allDates = [
      ...orderSales.map(s => s.date),
      ...mercadoPagoSales.map(s => s.date),
      ...physicalSales.map(s => s.date)
    ].filter(d => d && (d instanceof Date) && !isNaN(d.getTime()));
    if (allDates.length === 0) return null;
    const min = new Date(Math.min(...allDates.map(d => d.getTime())));
    const max = new Date(Math.max(...allDates.map(d => d.getTime())));
    return { min, max };
  }, [orderSales, mercadoPagoSales, physicalSales]);

  const copyEmailsToClipboard = useCallback((customers) => {
    const emails = Object.values(customers)
      .filter(c => c.email && c.email !== '')
      .map(c => c.email)
      .join(', ');
    
    if (emails) {
      navigator.clipboard.writeText(emails);
      setNotification({ message: `✅ ${emails.split(',').length} emails copiados al portapapeles`, show: true });
      setTimeout(() => setNotification({ message: '', show: false }), 3000);
    } else {
      setNotification({ message: '⚠️ No hay emails disponibles para copiar', show: true });
      setTimeout(() => setNotification({ message: '', show: false }), 3000);
    }
  }, []);


  const applyPreset = (presetFn) => {
    const { start, end } = presetFn();
    setStartDate(start);
    setEndDate(end);
    fetchSalesData(start, end);
  };

  return (
    <div className="sales-report">
      <div className="report-header">
        <h2>Panel de inteligencia empresarial</h2>
        <Notification message={notification.message} show={notification.show} />
      </div>
      
      <div className="filters">
        <div className="filters-inner">
          <div className="filters-dates">
            <label className="filter-label">
              <span className="filter-label-text">Desde</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="filter-input"
                aria-label="Fecha de inicio"
              />
            </label>
            <label className="filter-label">
              <span className="filter-label-text">Hasta</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="filter-input"
                aria-label="Fecha de fin"
              />
            </label>
          </div>
          <div className="filters-divider" aria-hidden="true" />
          <div className="filters-presets">
            <span className="filters-presets-label">Rango rápido</span>
            <div className="filters-preset-btns">
              <button type="button" className="filter-preset-btn" onClick={() => applyPreset(getPresetEsteMes)}>
                Este mes
              </button>
              <button type="button" className="filter-preset-btn" onClick={() => applyPreset(getPresetMesPasado)}>
                Mes pasado
              </button>
              <button type="button" className="filter-preset-btn" onClick={() => applyPreset(getPresetUltimos3Meses)}>
                Últimos 3 meses
              </button>
              <button type="button" className="filter-preset-btn" onClick={() => applyPreset(getPresetEsteAnio)}>
                Este año
              </button>
            </div>
          </div>
          <div className="filters-divider filters-divider--last" aria-hidden="true" />
          <div className="filters-actions">
            <button type="button" onClick={fetchSalesData} className="apply-button">
              Actualizar datos
            </button>
          </div>
          <span className="sales-report-dateRangeLabel" role="status">
            {actualDateRange
              ? `Los datos obtenidos son desde ${formatDateForLabel(actualDateRange.min)} hasta ${formatDateForLabel(actualDateRange.max)}.`
              : 'No hay datos en el período seleccionado.'}
          </span>
        </div>
      </div>

      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          📊 Dashboard
        </button>
        <button 
          className={`tab-button ${activeTab === 'demographic' ? 'active' : ''}`}
          onClick={() => setActiveTab('demographic')}
        >
          🌍 Demografía
        </button>
        <button 
          className={`tab-button ${activeTab === 'customers' ? 'active' : ''}`}
          onClick={() => setActiveTab('customers')}
        >
          👥 Clientes
        </button>
        <button 
          className={`tab-button ${activeTab === 'products' ? 'active' : ''}`}
          onClick={() => setActiveTab('products')}
        >
          📦 Productos
        </button>
        <button 
          className={`tab-button ${activeTab === 'temporal' ? 'active' : ''}`}
          onClick={() => setActiveTab('temporal')}
        >
          ⏰ Temporal
        </button>
      </div>

      {loading ? (
        <Spinner />
      ) : (
        <div className="report-content">
          {activeTab === 'dashboard' && (
            <SalesReportDashboardView
              analyticsData={analyticsData}
              chartTextColor={chartTextColor}
              uiTextColor={uiTextColor}
              startDate={startDate}
              endDate={endDate}
              categoryHighlightMetric={categoryHighlightMetric}
              setCategoryHighlightMetric={setCategoryHighlightMetric}
              categoryHighlightCount={categoryHighlightCount}
              setCategoryHighlightCount={setCategoryHighlightCount}
              productHighlightMetric={productHighlightMetric}
              setProductHighlightMetric={setProductHighlightMetric}
              productHighlightCount={productHighlightCount}
              setProductHighlightCount={setProductHighlightCount}
            />
          )}
          {activeTab === 'demographic' && (
            <SalesReportDemographicView
              analyticsData={analyticsData}
              chartTextColor={chartTextColor}
            />
          )}
          {activeTab === 'customers' && (
            <SalesReportCustomerView
              analyticsData={analyticsData}
              chartTextColor={chartTextColor}
              customerChannelTab={customerChannelTab}
              setCustomerChannelTab={setCustomerChannelTab}
              copyEmailsToClipboard={copyEmailsToClipboard}
            />
          )}
          {activeTab === 'products' && (
            <SalesReportProductView
              analyticsData={analyticsData}
              chartTextColor={chartTextColor}
              uiTextColor={uiTextColor}
              startDate={startDate}
              endDate={endDate}
              productTableSort={productTableSort}
              setProductTableSort={setProductTableSort}
              categoryTableSort={categoryTableSort}
              setCategoryTableSort={setCategoryTableSort}
              detailedAnalysisTab={detailedAnalysisTab}
              setDetailedAnalysisTab={setDetailedAnalysisTab}
              detailedItemsPerPage={detailedItemsPerPage}
              setDetailedItemsPerPage={setDetailedItemsPerPage}
              productTablePage={productTablePage}
              setProductTablePage={setProductTablePage}
              categoryTablePage={categoryTablePage}
              setCategoryTablePage={setCategoryTablePage}
            />
          )}
          {activeTab === 'temporal' && (
            <SalesReportTemporalView
              analyticsData={analyticsData}
              chartTextColor={chartTextColor}
            />
          )}
        </div>
      )}
    </div>
  );
};

const pad = (n) => String(n).padStart(2, '0');

/** Presets de rango de fechas (retornan { start, end } en formato YYYY-MM-DD) */
const getPresetEsteMes = () => {
  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return {
    start: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`,
    end: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(lastDay)}`,
  };
};

const getPresetMesPasado = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  return {
    start: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`,
    end: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(lastDay)}`,
  };
};

const getPresetUltimos3Meses = () => {
  const end = new Date();
  const start = new Date(end);
  start.setMonth(start.getMonth() - 2);
  start.setDate(1);
  return {
    start: `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`,
    end: `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`,
  };
};

const getPresetEsteAnio = () => {
  const now = new Date();
  return {
    start: `${now.getFullYear()}-01-01`,
    end: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
  };
};

export default SalesReport;